import React from 'react';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import * as yup from 'yup';
import * as techApi from '../../../lib/api/tech';
import * as fileApi from '../../../lib/api/file';
import TechMidForm from '../../../components/techmng/support/TechMidForm';
import { listMidTechs } from '../../../modules/techModule/techMids';
import SubTitleTemplate from '../../../components/common/template/SubTitleTemplate';
import TechMultiFileForm from '../../../components/techmng/support/TechMultiFileForm';
import DetailTemplate from '../../../components/common/template/DetailTemplate';
import { listTechFiles } from '../../../modules/techModule/techFiles';

const TechMidAddFormContainer = ({ project, tech, techMid, user, reloadTech }) => {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    let initialValues = {
        rptTitle: (techMid?.rptTitle) ? techMid.rptTitle : project?.cstNm,
        rptTyp: (techMid?.rptTyp) ? techMid.rptTyp : '',
        rptContext: '',
        rptStrtDt: '', //작업 시작 일
        rptStrtHr: '00', //작업 시작시간
        rptEndDt: '',
        rptEndHr: '00', //작업
        rptTotTm: 0,
        files: [{ file: null, fileDesc: '' }]
    }

    const validationSchema = yup.object({
        rptTitle: yup.string('').required(' 중간보고서명을 입력하세요'),
        rptTyp: yup.string().required(' 중간보고서 종류를 선택하세요'),
        rptStrtDt: yup
            .date()
            .required('작업 시작일을 입력하세요')
            .nullable()
            .default(undefined),
        rptEndDt: yup
            .date()
            .required('작업 종료일을 입력하세요')
            .min(yup.ref('rptStrtDt'), '작업 종료일을 확인하세요')
            .when('rptStrtDt', (rptStrtDt, schema) => {
                if (rptStrtDt) return schema.required('작업 종료일을 입력하세요');
            })
            .nullable()
            .default(undefined),
        rptContext: yup.string('').required(' 중간보고서 내용을 입력하세요'),
    });



    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);



            //(1) rpt 등록
            //(3) file 업로드    
            // console.log(dataToSubmit,'dataToSubmit');
            onSave(values);
            formik.resetForm();
            setSubmitting(false);
        },
    });

    const makeDataToSubmit = (values) => {
        const rptStrHour = values.rptStrtHr ? values.rptStrtHr + ':00:00' : '00:00:00';
        const rptEndHour = values.rptEndHr ? values.rptEndHr + ':00:00' : '00:00:00';

        const dataToSubmit = {
            reqUuid: tech.reqUuid,
            prjUuid: tech.prjUuid,
            planUuid: tech.planUuid,
            rptTitle: values.rptTitle,
            rptTyp: values.rptTyp,
            rptStrtDt: values.rptStrtDt
                ? dayjs(values.rptStrtDt).format('YYYY-MM-DD')
                : null,
            rptStrtTm: rptStrHour,
            rptEndDt: values.rptStrtDt
                ? dayjs(values.rptStrtDt).format('YYYY-MM-DD')
                : null,
            rptEndTm: rptEndHour,
            rptContext: values.rptContext,
            rptTotTm: values.rptTotTm,
        };


        return dataToSubmit;
    };

    const onSave = async (values) => {
        const dataToSubmit = makeDataToSubmit(values);

        try {
            const res = await techApi.addTechMid(dataToSubmit);

            if (res.data.info) {
                if (values.files.length > 0 && values.files[0].file !== null) {
                    values.files.forEach(value => {
                        onFileSave(value, tech);
                    })
                }
                const searchData = { "reqUuid": tech?.reqUuid, "planUuid": tech?.planUuid, "prjUuid": tech?.prjUuid };
                dispatch(listMidTechs(searchData))
                dispatch(listTechFiles('tech', tech.reqUuid));
            }
            enqueueSnackbar('기술지원 중간 보고서 저장 성공', {
                variant: 'success',
            });
        } catch (err) {
            let message =
                err.response !== 'undefined' ? err.response?.data?.message : err.message;
            enqueueSnackbar(message, {
                variant: 'error',
            });
        }
    };

    const onFileSave = async (value) => {
        let formData = new FormData();
        formData.append('fileDt', value.file);
        formData.append('fileTyp', 'tech');
        formData.append('fileAttrVal1', tech.reqUuid);
        formData.append('fileAttrVal2', tech.prjUuid);
        formData.append('fileAttrVal3', "기술 지원 중간 보고서");
        formData.append('fileDesc', value.fileDesc);


        try {
            const response = await fileApi.addFiles(formData);

            if (response.data.result === 'success') {
                enqueueSnackbar("파일 업로드 성공", {
                    variant: 'success',
                });
            }

        } catch (err) {
            let message =
                typeof err.response !== 'undefined'
                    ? err.response.data.message
                    : err.message;
            enqueueSnackbar(message, {
                variant: 'error',
            });
        }
    };

    return (
        <DetailTemplate title="중간보고서 작성">
            <Box
                component="form"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mt: 2,
                    }}
                >
                    <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                        저장
                    </Button>
                </Box>
                <SubTitleTemplate title="중간보고서">
                    <TechMidForm formik={formik} />
                </SubTitleTemplate>
                <SubTitleTemplate title="첨부 서류">
                    <FormikProvider value={formik}>
                        <FieldArray name="files"
                            render={arrayHelpers => (
                                <Box sx={{

                                }}>
                                    <Box>
                                        {formik.values?.files.map((f, index) => {
                                            return (
                                                <TechMultiFileForm
                                                    key={index}
                                                    file={f}
                                                    index={index}
                                                    touched={formik.touched}
                                                    errors={formik.errors}
                                                    handleChange={formik.handleChange}
                                                    handleBlur={formik.handleBlur}
                                                    handleRemove={() => arrayHelpers.remove(index)} />
                                            )
                                        })}
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', my: 2 }}>
                                        <Button sx={{ mx: 2 }} color="secondary" variant="outlined" onClick={() => arrayHelpers.push({ fileDesc: '', file: null })}>파일 추가</Button>
                                    </Box>
                                </Box>

                            )}

                        >

                        </FieldArray>
                    </FormikProvider>
                </SubTitleTemplate>
            </Box>
        </DetailTemplate>
    )
}

export default TechMidAddFormContainer